import { Field, Image, RadioGroup } from '@chakra-ui/react'
import { FormErrors } from 'commons/components/layout/FormErrors'
import { Section } from 'commons/components/layout/Section'
import { PaymentTypeButton } from 'commons/components/paymentType/PaymentTypeButton'
import { useFieldContext } from 'commons/config/form'
import { PaymentTypes } from 'commons/constants/paymentTypes'
import { TextColors } from 'commons/design-system/tokens'
import { useBrowserDetection } from 'commons/hooks/others/useBrowserDetection'
import type { FeatureFlippingResponse } from 'commons/types/featureFlippingTypes'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

export const PaymentTypeSection = ({
	featureFlippings,
	isFromMoneyPot = false,
	collectIban = '',
	hideLydia = false
}: {
	featureFlippings: FeatureFlippingResponse | undefined
	isFromMoneyPot?: boolean
	collectIban?: string
	hideLydia?: boolean
}) => {
	const field = useFieldContext<string>()
	const { t } = useTranslation()
	const assetsBaseUrl = isFromMoneyPot
		? `${process.env.REACT_APP_POTS_URL}collect/assets/img/payment`
		: `${process.env.REACT_APP_PAYMENT_FORM_URL}l/assets/img/payment`

	const isCkoFlowEnabled = useMemo(() => {
		return featureFlippings && featureFlippings.enable_cko_flow
	}, [featureFlippings])

	const { isSafari, isChrome, isFirefox } = useBrowserDetection()

	const ckoFlowImage = useMemo(() => {
		if (isSafari) {
			return `${assetsBaseUrl}/applepay.png`
		}
		return `${assetsBaseUrl}/cko_flow.png`
	}, [isFromMoneyPot, isSafari])

	const ckoFlowText = useMemo(() => {
		if (isSafari) {
			return t('phone_form.pay_with_xpay_apple_only')
		} else return t('phone_form.pay_with_xpay')
	}, [isSafari, isChrome, isFirefox, t])

	const changePaymentType = (value: PaymentTypes) => {
		field.handleChange(value)
	}

	return (
		<Field.Root invalid={!field.state.meta.isValid}>
			<Section
				_groupInvalid={{
					outline: `2px solid ${TextColors.STATUS_ERROR}`
				}}
			>
				<RadioGroup.Root
					name={field.name}
					value={field.state.value}
					onValueChange={({ value }: { value: string | null }) =>
						changePaymentType((value as PaymentTypes) ?? '')
					}
					display='flex'
					flexDirection='column'
					gap={3}
				>
					{!hideLydia && (
						<PaymentTypeButton value={PaymentTypes.LYDIA}>
							{t('phone_form.pay_with_lydia')}
							<Image
								src={
									isFromMoneyPot
										? `${process.env.REACT_APP_POTS_URL}collect/assets/img/logo_app_lydia.png`
										: `${process.env.REACT_APP_PAYMENT_FORM_URL}l/assets/img/logo_app_lydia.png`
								}
								alt='Lydia Logo'
								w='auto'
								h='12px'
							/>
						</PaymentTypeButton>
					)}
					<PaymentTypeButton value={PaymentTypes.CARD}>
						{t('phone_form.pay_with_card')}
						<Image
							src={
								isFromMoneyPot
									? `${process.env.REACT_APP_POTS_URL}collect/assets/img/payment/card-payment-methods.png`
									: `${process.env.REACT_APP_PAYMENT_FORM_URL}l/assets/img/payment/card-payment-methods.png`
							}
							alt='Cards Logo'
							w='auto'
							h='12px'
						/>
					</PaymentTypeButton>
					{isCkoFlowEnabled && !isChrome && !isFirefox && (
						<PaymentTypeButton value={PaymentTypes.XPAY}>
							{ckoFlowText}
							<Image src={ckoFlowImage} alt='XPay Logo' w='auto' h='18px' />
						</PaymentTypeButton>
					)}
					{isCkoFlowEnabled && (isChrome || isFirefox) && (
						<>
							<PaymentTypeButton value={PaymentTypes.XPAY_GOOGLE}>
								{t('phone_form.pay_with_xpay_google_only')}
								<Image src={`${assetsBaseUrl}/gpay.png`} alt='Google Pay Logo' w='auto' h='18px' />
							</PaymentTypeButton>
							<PaymentTypeButton value={PaymentTypes.XPAY_APPLE}>
								{t('phone_form.pay_with_xpay_apple_only')}
								<Image
									src={`${assetsBaseUrl}/applepay.png`}
									alt='Apple Pay Logo'
									w='auto'
									h='18px'
								/>
							</PaymentTypeButton>
						</>
					)}
					{collectIban !== '' && (
						<PaymentTypeButton value={PaymentTypes.TRANSFER}>
							{t('phone_form.pay_with_transfer')}
							<Image
								src={
									isFromMoneyPot
										? `${process.env.REACT_APP_POTS_URL}collect/assets/img/payment/ico/bank.png`
										: `${process.env.REACT_APP_PAYMENT_FORM_URL}l/assets/img/payment/ico/bank.png`
								}
								alt='Bank Logo'
								w='auto'
								h='12px'
							/>
						</PaymentTypeButton>
					)}
				</RadioGroup.Root>
				<FormErrors errors={field.state.meta.errors} />
			</Section>
		</Field.Root>
	)
}
